import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Events that signal the media element has made progress and is worth another play attempt.
 * None of these are registered with `{ once: true }` — WebKit can reject a play attempt at any
 * point in the loading sequence, so every milestone gets a fresh try.
 */
const PROGRESS_EVENTS = ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'stalled', 'suspend', 'pause'];

/**
 * Gestures that unlock playback once Safari has decided a user interaction is required.
 * Registered on the document with `capture` so they fire regardless of where the gesture lands.
 * `scroll` is deliberately absent: it is not a user activation, so retrying on it can never succeed
 * and only ran a refused play() on every scroll frame.
 */
const GESTURE_EVENTS = ['pointerdown', 'touchend', 'click', 'keydown'];

/** How long to wait after a refused play attempt before settling on the poster. */
const BLOCKED_TIMEOUT = 3000;

/**
 * A custom element that keeps a muted background or hero video playing.
 *
 * Autoplay is expected to be handled natively by the `autoplay muted playsinline` attributes in the
 * markup — this component is the resilience layer for iOS Safari, which defers or refuses autoplay
 * for reasons the page cannot observe (Low Power Mode, cellular heuristics, per-site autoplay
 * settings).
 *
 * The host element carries a `data-state` attribute (`idle` | `playing` | `blocked`) so CSS can
 * cross-fade the video over its poster, and leave the poster in place when playback never starts.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The video element.
 * @property {HTMLSourceElement[]} [videoSources] - Sources with a deferred `data-video-source` URL.
 *
 * @extends Component<Refs>
 */
export class VideoAutoplayComponent extends Component {
  requiredRefs = ['video'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {boolean} Whether the element is currently within (or near) the viewport. */
  #visible = false;

  /** @type {boolean} Whether a play() attempt is currently in flight. */
  #pending = false;

  /** @type {boolean} Whether deferred sources have been promoted to real `src` attributes. */
  #sourcesLoaded = false;

  /** @type {boolean} Whether gesture-unlock listeners are currently attached. */
  #gestureArmed = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #blockedTimer;

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;

    if (!(video instanceof HTMLVideoElement)) return;

    // WebKit re-reads these as properties; the HTML attributes alone are not always enough once the
    // element has been touched by script. Deliberately not re-setting `autoplay` — the attribute is
    // already in the markup and native autoplay must remain the primary path.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    if (!this.dataset.state) this.dataset.state = 'idle';

    if (prefersReducedMotion()) {
      // Leave the poster in place rather than hiding the video outright, which would expose the
      // container background.
      this.dataset.state = 'blocked';
      return;
    }

    for (const event of PROGRESS_EVENTS) {
      video.addEventListener(event, this.#handleProgress);
    }

    video.addEventListener('playing', this.#handlePlaying);

    window.addEventListener('pageshow', this.#handleWake);
    document.addEventListener('visibilitychange', this.#handleWake);

    this.#observer = new IntersectionObserver(this.#handleIntersect, { rootMargin: '200px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { video } = this.refs;

    this.#observer?.disconnect();
    this.#disarmGesture();
    clearTimeout(this.#blockedTimer);

    window.removeEventListener('pageshow', this.#handleWake);
    document.removeEventListener('visibilitychange', this.#handleWake);

    if (video instanceof HTMLVideoElement) {
      for (const event of PROGRESS_EVENTS) {
        video.removeEventListener(event, this.#handleProgress);
      }
      video.removeEventListener('playing', this.#handlePlaying);
    }
  }

  /**
   * Promotes `data-video-source` placeholders to real `src` attributes.
   *
   * Used for below-the-fold background videos so their bytes are not fetched until they are near the
   * viewport. Hero videos ship real `src` attributes and never take this path.
   */
  #loadDeferredSources() {
    if (this.#sourcesLoaded) return;

    const { video, videoSources } = this.refs;

    if (!(video instanceof HTMLVideoElement)) return;

    this.#sourcesLoaded = true;

    const sources = Array.isArray(videoSources) ? videoSources : [];
    let promoted = false;

    for (const source of sources) {
      const url = source instanceof HTMLElement ? source.dataset.videoSource : null;

      if (url && !source.getAttribute('src')) {
        source.setAttribute('src', url);
        promoted = true;
      }
    }

    if (promoted) video.load();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    const entry = entries[entries.length - 1];

    if (!entry) return;

    const { video } = this.refs;

    this.#visible = entry.isIntersecting;

    if (entry.isIntersecting) {
      this.#loadDeferredSources();
      this.#tryPlay();
    } else if (video instanceof HTMLVideoElement && !video.paused) {
      // Pausing off-screen video keeps the decoder idle. This also matches WebKit's own policy,
      // which suspends playback of videos that scroll out of view.
      video.pause();
    }
  };

  #handleProgress = () => {
    if (this.#visible) this.#tryPlay();
  };

  #handlePlaying = () => {
    clearTimeout(this.#blockedTimer);
    this.dataset.state = 'playing';
    // Playback is genuinely running, so the gesture fallback is no longer needed.
    this.#disarmGesture();
  };

  #handleWake = () => {
    if (document.hidden) return;
    if (this.#visible) this.#tryPlay();
  };

  /** Attempts playback, arming the gesture fallback if the browser refuses. */
  async #tryPlay() {
    const { video } = this.refs;

    if (!(video instanceof HTMLVideoElement)) return;
    if (this.#pending || !video.paused) return;

    this.#pending = true;

    try {
      await video.play();
      // `playing` sets the state — reaching here only means the promise resolved.
    } catch {
      // Autoplay refused. Keep the poster visible and wait for a gesture.
      this.#armGesture();
      this.#scheduleBlocked();
    } finally {
      this.#pending = false;
    }
  }

  /**
   * Marks the video as blocked if playback has not actually started shortly after being refused.
   * This is the iOS Low Power Mode path: the poster stays, and nothing visibly fails.
   */
  #scheduleBlocked() {
    clearTimeout(this.#blockedTimer);

    this.#blockedTimer = setTimeout(() => {
      const { video } = this.refs;

      if (video instanceof HTMLVideoElement && video.paused) {
        this.dataset.state = 'blocked';
      }
    }, BLOCKED_TIMEOUT);
  }

  #armGesture() {
    if (this.#gestureArmed) return;

    this.#gestureArmed = true;

    for (const event of GESTURE_EVENTS) {
      document.addEventListener(event, this.#handleGesture, { passive: true, capture: true });
    }
  }

  #disarmGesture() {
    if (!this.#gestureArmed) return;

    this.#gestureArmed = false;

    for (const event of GESTURE_EVENTS) {
      document.removeEventListener(event, this.#handleGesture, { capture: true });
    }
  }

  /**
   * Retries on the first user gesture.
   *
   * The listeners are intentionally left attached until a `playing` event actually fires. Removing
   * them on the first gesture — regardless of whether playback started — permanently disables the
   * fallback when the very first tap happens while autoplay is still blocked.
   */
  #handleGesture = () => {
    this.#tryPlay();
  };
}

if (!customElements.get('video-autoplay')) {
  customElements.define('video-autoplay', VideoAutoplayComponent);
}

export default VideoAutoplayComponent;
